import React from 'react';
import { MapPin } from 'lucide-react';
import type { ReportItem, ReportSection } from '@/types';
import { sortByOrder, formatCurrency } from '@/utils/helpers';
import AnnotationOverlay from '@/components/AnnotationOverlay';
import StatusTable from '@/components/portal/StatusTable';
import ComponentAttributes from '@/components/portal/ComponentAttributes';
import SectionLimitationsBlock from '@/components/portal/SectionLimitationsBlock';
import {
  Card, CardHeader, CardTitle, SEVERITY, SeverityDot, SeverityPill, severityKey,
} from '@/ui';

// Per-section card with the Information / Standards tabs and per-finding
// rows. Lifted out of ClientPortal.tsx during the 2026-05-01 split. Two
// helper components (SectionTabbedBody, FindingRow) live in this file
// because SectionCard composes them tightly and they're not used elsewhere.

export const SectionCard: React.FC<{
  section: ReportSection;
  onTogglePhoto: (id: string) => void;
  expandedPhotos: Record<string, boolean>;
  assignRef: (el: HTMLDivElement | null) => void;
}> = ({ section, onTogglePhoto, expandedPhotos, assignRef }) => {
  const items = sortByOrder(section.items || []);
  const components = section.components || [];
  const attributeGroups = section.attributes || [];

  // Separate the General component — it's section metadata, not an
  // inspectable peer component. Its content renders as a contextual
  // "Information" block at the top of the section instead.
  const generalComp = components.find(
    c => c.name.trim().toLowerCase() === 'general',
  );
  const nonGeneralComponents = components.filter(c => c !== generalComp);

  const generalAttrGroup = generalComp
    ? attributeGroups.find(a => a.componentId === generalComp.id) ?? null
    : null;
  const generalNotes = generalComp?.notes ?? [];

  const itemsByComponent = new Map<string, ReportItem[]>();
  const generalItems: ReportItem[] = [];
  for (const item of items) {
    if (item.componentId) {
      const list = itemsByComponent.get(item.componentId) || [];
      list.push(item);
      itemsByComponent.set(item.componentId, list);
    } else {
      generalItems.push(item);
    }
  }

  // Items linked to the General component via findings
  const generalCompItems = generalComp
    ? itemsByComponent.get(generalComp.id) ?? []
    : [];

  // Documentation-photo items for General (title "General", no componentId,
  // no severity — created by report generation from component_photos).
  const generalDocItems = generalItems.filter(
    i => i.title.trim().toLowerCase() === 'general' && !i.severity,
  );
  const remainingGeneralItems = generalItems.filter(
    i => !(i.title.trim().toLowerCase() === 'general' && !i.severity),
  );

  // All photos belonging to the General component (doc photos + finding photos)
  const generalPhotos = [
    ...generalDocItems.flatMap(i => i.photos ?? []),
    ...generalCompItems.flatMap(i => i.photos ?? []),
  ];

  const hasGeneralInfo =
    generalPhotos.length > 0 ||
    (generalAttrGroup && generalAttrGroup.pairs.length > 0) ||
    generalNotes.length > 0;

  // Match remaining general items (doc-photo items) to their component by
  // title so they render inside the component block, not separately.
  const docItemsByCompName = new Map<string, ReportItem[]>();
  const unmatchedGeneralItems: ReportItem[] = [];
  const compNames = new Set(nonGeneralComponents.map(c => c.name.trim().toLowerCase()));
  for (const item of remainingGeneralItems) {
    const key = item.title.trim().toLowerCase();
    if (compNames.has(key)) {
      const list = docItemsByCompName.get(key) || [];
      list.push(item);
      docItemsByCompName.set(key, list);
    } else {
      unmatchedGeneralItems.push(item);
    }
  }

  const componentBlocks = nonGeneralComponents
    .map(comp => {
      const attrGroup = attributeGroups.find(a => a.componentId === comp.id) || null;
      const compItems = itemsByComponent.get(comp.id) || [];
      const notes = comp.notes ?? [];
      const docItems = docItemsByCompName.get(comp.name.trim().toLowerCase()) || [];
      if (!attrGroup && compItems.length === 0 && notes.length === 0 && docItems.length === 0) return null;
      return { comp, attrGroup, compItems, notes, docItems };
    })
    .filter(
      (
        b,
      ): b is {
        comp: typeof components[number];
        attrGroup: typeof attributeGroups[number] | null;
        compItems: ReportItem[];
        notes: string[];
        docItems: ReportItem[];
      } => b !== null,
    );

  const hasComponentBlocks = componentBlocks.length > 0;
  const sectionLimitations = section.limitations ?? [];
  const sopBody = (section.sopBody ?? '').trim();
  const hasSop = sopBody.length > 0;

  return (
    <div
      ref={assignRef}
      data-section-id={section.id}
      className="scroll-mt-36"
    >
      <Card variant="elevated" unpadded>
        <CardHeader>
          <div>
            <CardTitle as="h2" className="text-heading-lg font-display" {...{ style: { fontVariationSettings: "'opsz' 144, 'SOFT' 40" } }}>
              {section.title}
            </CardTitle>
            <p className="text-caption text-ink-subtle mt-0.5">
              {items.length} item{items.length === 1 ? '' : 's'}
            </p>
          </div>
        </CardHeader>

        <SectionTabbedBody
          showTabs={hasSop}
          sopBody={sopBody}
          information={
            <>
              {hasGeneralInfo && (
                <div className="px-5 md:px-6 pt-3 pb-3">
                  {generalPhotos.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                      {generalPhotos.map(photo => (
                        <button
                          key={photo.id}
                          type="button"
                          className={
                            'group relative rounded-lg overflow-hidden bg-surface-1 border border-border-subtle ' +
                            'transition-transform duration-180 ease-premium hover:-translate-y-0.5 hover:shadow-card ' +
                            (expandedPhotos[photo.id] ? 'col-span-2 sm:col-span-3' : '')
                          }
                          onClick={() => onTogglePhoto(photo.id)}
                          aria-label={expandedPhotos[photo.id] ? 'Collapse photo' : 'Expand photo'}
                        >
                          <div className="relative">
                            <img
                              src={photo.url}
                              alt={photo.caption || `${section.title} overview`}
                              className="w-full h-auto block"
                            />
                            <AnnotationOverlay annotations={photo.annotations ?? []} />
                          </div>
                          {photo.caption && (
                            <p className="px-3 py-2 text-caption text-ink-subtle bg-surface-1 border-t border-border-subtle text-left">
                              {photo.caption}
                            </p>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  {generalPhotos.length === 0 && section.mainPhotoUrl && (
                    <div className="mb-3">
                      <img
                        src={section.mainPhotoUrl}
                        alt={`${section.title} overview`}
                        className="w-full h-auto rounded-lg border border-border-subtle"
                      />
                    </div>
                  )}
                  {generalAttrGroup && generalAttrGroup.pairs.length > 0 && (
                    <div className="mb-2">
                      <ComponentAttributes pairs={generalAttrGroup.pairs} />
                    </div>
                  )}
                  {generalNotes.length > 0 && (
                    <div className="space-y-2 text-body-sm text-ink">
                      {generalNotes.map((body, i) => (
                        <p key={i} className="whitespace-pre-wrap leading-relaxed">
                          {body}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {!hasGeneralInfo && section.mainPhotoUrl && (
                <div className="px-5 md:px-6 pt-3 pb-3">
                  <img
                    src={section.mainPhotoUrl}
                    alt={`${section.title} overview`}
                    className="w-full h-auto rounded-lg border border-border-subtle"
                  />
                </div>
              )}

              {nonGeneralComponents.length > 0 && (
                <div className="px-5 md:px-6 pt-1 pb-3">
                  <StatusTable components={nonGeneralComponents} />
                </div>
              )}

              {sectionLimitations.length > 0 && (
                <div className="px-5 md:px-6 pb-3">
                  <SectionLimitationsBlock limitations={sectionLimitations} />
                </div>
              )}

              {(unmatchedGeneralItems.length > 0 || hasComponentBlocks) && (
                <div className="divide-y divide-border-subtle border-t border-border-subtle">
                  {unmatchedGeneralItems.map(item => (
                    <ul key={item.id} className="divide-y divide-border-subtle">
                      <FindingRow
                        item={item}
                        onTogglePhoto={onTogglePhoto}
                        expandedPhotos={expandedPhotos}
                      />
                    </ul>
                  ))}
                  {componentBlocks.map(({ comp, attrGroup, compItems, notes, docItems }) => (
                    <section key={comp.id} className="py-4" aria-label={comp.name}>
                      <h3 className="px-5 md:px-6 text-heading-sm font-semibold text-ink">
                        {comp.name}
                      </h3>
                      {docItems.length > 0 && (
                        <div className="px-5 md:px-6 mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {docItems.flatMap(di => di.photos ?? []).map(photo => (
                            <button
                              key={photo.id}
                              type="button"
                              className={
                                'group relative rounded-lg overflow-hidden bg-surface-1 border border-border-subtle ' +
                                'transition-transform duration-180 ease-premium hover:-translate-y-0.5 hover:shadow-card ' +
                                (expandedPhotos[photo.id] ? 'col-span-2 sm:col-span-3' : '')
                              }
                              onClick={() => onTogglePhoto(photo.id)}
                              aria-label={expandedPhotos[photo.id] ? 'Collapse photo' : 'Expand photo'}
                            >
                              <div className="relative">
                                <img
                                  src={photo.url}
                                  alt={photo.caption || comp.name}
                                  className="w-full h-auto block"
                                />
                                <AnnotationOverlay annotations={photo.annotations ?? []} />
                              </div>
                              {photo.caption && (
                                <p className="px-3 py-2 text-caption text-ink-subtle bg-surface-1 border-t border-border-subtle text-left">
                                  {photo.caption}
                                </p>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                      {attrGroup && attrGroup.pairs.length > 0 && (
                        <div className="px-5 md:px-6 mt-2">
                          <ComponentAttributes pairs={attrGroup.pairs} />
                        </div>
                      )}
                      {notes.length > 0 && (
                        <div className="px-5 md:px-6 mt-3 space-y-2 text-body-sm text-ink">
                          {notes.map((body, i) => (
                            <p key={i} className="whitespace-pre-wrap leading-relaxed">
                              {body}
                            </p>
                          ))}
                        </div>
                      )}
                      {compItems.length > 0 && (
                        <ul className="mt-3 divide-y divide-border-subtle border-t border-border-subtle">
                          {compItems.map(item => (
                            <FindingRow
                              key={item.id}
                              item={item}
                              onTogglePhoto={onTogglePhoto}
                              expandedPhotos={expandedPhotos}
                            />
                          ))}
                        </ul>
                      )}
                    </section>
                  ))}
                </div>
              )}
            </>
          }
        />
      </Card>
    </div>
  );
};

/**
 * Information / Standards sub-tab strip per Spectora's section pattern. When
 * the section has no SoP body (the inspector cleared it on the template),
 * render the information body directly without the tab chrome.
 */
const SectionTabbedBody: React.FC<{
  showTabs: boolean;
  sopBody: string;
  information: React.ReactNode;
}> = ({ showTabs, sopBody, information }) => {
  const [active, setActive] = React.useState<'information' | 'standards'>('information');

  if (!showTabs) {
    return <>{information}</>;
  }

  return (
    <div>
      <div
        role="tablist"
        aria-label="Section view"
        className="flex gap-1 px-5 md:px-6 pt-2 border-b border-border-subtle"
      >
        {(['information', 'standards'] as const).map((id) => {
          const isActive = active === id;
          const label = id === 'information' ? 'Information' : 'Standards';
          return (
            <button
              key={id}
              role="tab"
              type="button"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setActive(id)}
              className={
                'px-3 py-2 text-caption font-medium tracking-wide uppercase transition-colors ' +
                (isActive
                  ? 'text-brand border-b-2 border-brand -mb-px'
                  : 'text-ink-subtle hover:text-ink')
              }
            >
              {label}
            </button>
          );
        })}
      </div>
      {active === 'information' ? (
        information
      ) : (
        <div className="px-5 md:px-6 py-4">
          <p className="text-body-sm text-ink leading-relaxed whitespace-pre-wrap">
            {sopBody}
          </p>
          <p className="mt-3 text-caption text-ink-subtle">
            Applicable Standards of Practice for this section, snapshotted when the report was generated.
          </p>
        </div>
      )}
    </div>
  );
};

const FindingRow: React.FC<{
  item: ReportItem;
  onTogglePhoto: (id: string) => void;
  expandedPhotos: Record<string, boolean>;
}> = ({ item, onTogglePhoto, expandedPhotos }) => {
  const token = SEVERITY[severityKey(item.severity)];
  return (
    <li className="relative px-5 md:px-6 py-5 transition-colors duration-180 hover:bg-surface-2">
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <SeverityDot severity={item.severity} />
              <h3 className="text-heading-sm text-ink">{item.title}</h3>
              <SeverityPill severity={item.severity} compact hideDot />
            </div>
          </div>

          {item.description && (
            <p className="mt-2 text-body-md text-ink-muted whitespace-pre-line max-w-prose-wide">
              {item.description}
            </p>
          )}

          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 items-center">
            {item.location && (
              <span className="inline-flex items-center gap-1.5 text-caption text-ink-subtle">
                <MapPin className="h-3.5 w-3.5" />
                {item.location}
              </span>
            )}
            {item.estimatedCost != null && item.estimatedCost > 0 && (
              <span className="inline-flex items-center gap-1.5 text-caption text-ink-muted">
                <span className={'inline-block h-1.5 w-1.5 rounded-full ' + token.classes.dot} aria-hidden />
                Est. {formatCurrency(item.estimatedCost)}
              </span>
            )}
            {item.suggestedTrades && item.suggestedTrades.length > 0 && (
              <span className="inline-flex items-center gap-1.5 flex-wrap">
                {item.suggestedTrades.map(trade => (
                  <span
                    key={trade}
                    className="inline-flex items-center text-caption text-ink-subtle bg-surface-2 border border-border-subtle rounded-full px-2.5 py-0.5"
                  >
                    {trade}
                  </span>
                ))}
              </span>
            )}
          </div>

          {item.photos && item.photos.length > 0 && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {item.photos.map(photo => (
                <button
                  key={photo.id}
                  type="button"
                  className={
                    'group relative rounded-xl overflow-hidden bg-surface-2 border border-border-subtle ' +
                    'transition-transform duration-180 ease-premium hover:-translate-y-0.5 hover:shadow-card ' +
                    (expandedPhotos[photo.id] ? 'col-span-2 sm:col-span-3' : '')
                  }
                  onClick={() => onTogglePhoto(photo.id)}
                  aria-label={expandedPhotos[photo.id] ? 'Collapse photo' : 'Expand photo'}
                >
                  <div className="relative">
                    <img
                      src={photo.url}
                      alt={photo.caption || item.title}
                      className="w-full h-auto block"
                    />
                    <AnnotationOverlay annotations={photo.annotations ?? []} />
                    <span className="absolute inset-0 bg-gradient-to-t from-ink/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-180" aria-hidden />
                  </div>
                  {photo.caption && (
                    <p className="px-3 py-2 text-caption text-ink-subtle bg-surface-1 border-t border-border-subtle text-left">
                      {photo.caption}
                    </p>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </li>
  );
};
